import React, { useEffect } from 'react';
import { MdDeleteOutline } from 'react-icons/md';

import { UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { addNotification, Loading, Notification } from '@lucidhq/lucidium';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  Flex,
  RadioGroup,
  Text,
  Icon,
  Label,
  useToggle,
  Box
} from '@lucidhq/lucidium';
import { Field, Form } from '@lucidhq/lucidium/components/FormV2';
import { BackgroundColorProperty } from '@lucidhq/lucidium/lss/csstypes';

import { ForcedExposureCreativeResponseType } from '~/components/Shared/hooks/useFetchForcedExposureCreatives';
import { useUpsertForcedExposureCreative } from '~/components/Shared/hooks/useMutateForcedExposureCreative';
import { InfoTooltip } from '~/impact-measurement/components';
import { t } from '~/utils/i18n';

import { useCampaignManager } from '../../CampaignManagerProvider';
import { ErrorBanner } from './ErrorBanner';
import { FileTypeInfo } from './FileTypeInfo';
import { UploadButton } from './UploadButton';
import { useFileUpload } from './useFileUplaod';

export enum EXPOSURE_TYPE {
  SIMPLE_EXPOSURE = 'SIMPLE_EXPOSURE'
}

export enum EXPOSURE_DEVICE {
  MOBILE = 'MOBILE',
  DESKTOP = 'DESKTOP'
}

type MediaForm = {
  creativeId?: string;
  exposureType: EXPOSURE_TYPE;
  exposureDevice: EXPOSURE_DEVICE;
  displayName: string;
};

type UploadMediaDialogProps = {
  initValues?: MediaForm;
  fileName?: string;
  updatedAt?: string;
  refetchCreatives: () => Promise<
    UseQueryResult<ForcedExposureCreativeResponseType>
  >;
  children: ({ onToggle }: { onToggle: () => void }) => React.ReactNode;
};

export const UploadMediaDialog = ({
  initValues,
  fileName,
  updatedAt,
  refetchCreatives,
  children
}: UploadMediaDialogProps) => {
  const isEditMode = Boolean(initValues);

  const [isOpen, handleToggle] = useToggle(false);
  const {
    error,
    file,
    inputEl,
    removeFile,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    isDragging,
    openPicker
  } = useFileUpload();

  const { managedCampaign } = useCampaignManager();

  const { mutate, isLoading } = useUpsertForcedExposureCreative(
    managedCampaign?.details.campaign_id,
    initValues?.creativeId
  );

  const uploadFileContent = (
    <>
      {error ? (
        <ErrorBanner type={error} />
      ) : (
        <Box>
          <Flex lss={{ gap: '4px' }}>
            <Text>
              {t('campaignManager.media.uploadModal.uploadFileBelow')}
            </Text>
            <Text lss={{ fontWeight: 'bold' }}>
              {t('campaignManager.media.uploadModal.onlyOneFile')}
            </Text>
          </Flex>

          <FileTypeInfo />
        </Box>
      )}

      <Flex
        lss={{
          padding: '0px 80px'
        }}
      >
        <UploadButton
          onClick={openPicker}
          isDragging={isDragging}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        />
      </Flex>
      {inputEl}
    </>
  );

  const handleSubmit = async (values: MediaForm) => {
    if (!isEditMode && !file) return;

    const fd = new FormData();
    fd.append('display_name', values.displayName.trim());
    fd.append('exposure_type', values.exposureType);
    fd.append('exposure_device', values.exposureDevice);
    if (file) fd.append('file', file, file.name);

    const payload = isEditMode
      ? {
          display_name: values.displayName,
          exposure_type: values.exposureType,
          exposure_device: values.exposureDevice
        }
      : fd;

    await mutate(payload, {
      onSuccess: async () => {
        removeFile();
        handleToggle();
        await refetchCreatives();
      },
      onError: error => {
        handleToggle();

        const msg = isAxiosError(error)
          ? error.message
          : t('campaignManager.media.uploadModal.errors.defaultError');

        addNotification(
          <Notification palette="danger" autoDismissSeconds={3}>
            {t(
              `campaignManager.media.uploadModal.errors.${
                isEditMode ? 'updateFailed' : 'createFailed'
              }`
            )}
            <br />
            {msg}
          </Notification>
        );
      }
    });
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isLoading) return;
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isLoading]);

  const loadingContent = (
    <Flex
      lss={{
        flexDirection: 'column',
        gap: '3.375rem',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '8rem 0rem'
      }}
    >
      <Flex lss={{ color: 'secondary500' }}>
        <Loading spinnerSize="lg" />
      </Flex>

      <Flex
        lss={{ flexDirection: 'column', gap: '0.25rem', textAlign: 'center' }}
      >
        <Text lss={{ fontSize: '1.5rem' }}>
          {t('campaignManager.media.uploadModal.loading.title')}
        </Text>
        <Text lss={{ fontSize: '1rem' }} as="strong">
          {t('campaignManager.media.uploadModal.loading.description')}
        </Text>
      </Flex>
    </Flex>
  );

  const isSaveDisabled = isEditMode ? isLoading : isLoading || !file;
  const isCloseDisabled = isLoading && !initValues;

  return (
    <>
      {children({ onToggle: handleToggle })}
      <Dialog
        data-testid="upload-creative-dialog"
        isOpen={isOpen}
        onClose={
          isCloseDisabled
            ? undefined
            : () => {
                removeFile();
                handleToggle();
              }
        }
        lss={{ minWidth: '50rem' }}
      >
        <DialogHeader
          palette="disabled"
          onClose={
            isCloseDisabled
              ? undefined
              : () => {
                  removeFile();
                  handleToggle();
                }
          }
        >
          {t('campaignManager.media.uploadModal.title')}
        </DialogHeader>
        <DialogContent>
          {isLoading && !initValues ? (
            loadingContent
          ) : (
            <Form
              initialValues={
                initValues ?? {
                  exposureType: EXPOSURE_TYPE.SIMPLE_EXPOSURE,
                  exposureDevice: EXPOSURE_DEVICE.MOBILE,
                  displayName: ''
                }
              }
              enableReinitialize
              onSubmit={handleSubmit}
            >
              <Box
                lss={{
                  borderBottom: 'regular',
                  borderColor: 'gray300'
                }}
              >
                <Field
                  id="exposureType"
                  name="exposureType"
                  required
                  label={t(
                    'campaignManager.media.uploadModal.exposureType.label'
                  )}
                  render={fieldProps => (
                    <RadioGroup
                      {...fieldProps}
                      value={fieldProps.value}
                      isInline
                      options={[
                        {
                          label: t(
                            'campaignManager.media.uploadModal.exposureType.option'
                          ),
                          value: EXPOSURE_TYPE.SIMPLE_EXPOSURE
                        }
                      ]}
                    />
                  )}
                />
                <Field
                  id="exposureDevice"
                  name="exposureDevice"
                  required
                  label={t(
                    'campaignManager.media.uploadModal.exposureDevice.label'
                  )}
                  render={fieldProps => (
                    <RadioGroup
                      {...fieldProps}
                      value={fieldProps.value}
                      options={[
                        {
                          label: t(
                            'campaignManager.media.uploadModal.exposureDevice.mobile'
                          ),
                          value: EXPOSURE_DEVICE.MOBILE
                        },
                        {
                          label: t(
                            'campaignManager.media.uploadModal.exposureDevice.desktop'
                          ),
                          value: EXPOSURE_DEVICE.DESKTOP
                        }
                      ]}
                    />
                  )}
                />
                <Box>
                  <Flex lss={{ gap: '2px' }}>
                    <Label htmlFor="displayName" lss={{ mr: 'xxs' }} required>
                      {t('campaignManager.media.uploadModal.displayName.label')}{' '}
                    </Label>
                    <InfoTooltip
                      title={t(
                        'campaignManager.media.uploadModal.displayName.tooltip'
                      )}
                      placement="right-start"
                      iconLss={{ height: '1.125rem', width: '1.125rem' }}
                    />
                  </Flex>
                </Box>
                <Field
                  id="displayName"
                  required
                  name="displayName"
                  lss={{
                    maxWidth: '38rem'
                  }}
                />
              </Box>

              <Flex
                lss={{
                  flexDirection: 'column',
                  gap: '40px',
                  padding: '24px 0px'
                }}
              >
                {initValues ? (
                  <Flex
                    data-testid="file-item"
                    lss={{
                      justifyContent: 'flex-start',
                      padding: '4px 12px',
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Flex lss={{ flexDirection: 'column' }}>
                      <Text lss={{ fontWeight: 'bold' }}>{fileName}</Text>
                      <Text lss={{ textScale: 'control', color: 'gray700' }}>
                        {new Date(updatedAt ?? '').toLocaleDateString(
                          'default',
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            timeZone: 'UTC'
                          }
                        )}
                      </Text>
                    </Flex>
                  </Flex>
                ) : (
                  <>
                    {file ? (
                      <Flex lss={{ flexDirection: 'column' }}>
                        <Flex
                          data-testid="file-item"
                          lss={{
                            borderRadius: 'md',
                            justifyContent: 'space-between',
                            padding: '4px 12px',
                            alignItems: 'center',
                            cursor: 'pointer',
                            width: '100%',
                            // @ts-ignore
                            '&:hover': {
                              backgroundColor: '#efefef' as BackgroundColorProperty,
                              '.delete-btn': {
                                display: 'flex'
                              }
                            }
                          }}
                        >
                          <Flex lss={{ flexDirection: 'column' }}>
                            <Text lss={{ fontWeight: 'bold' }}>
                              {file.name}
                            </Text>
                            <Text
                              lss={{ textScale: 'control', color: 'gray700' }}
                            >
                              {new Date(file.lastModified).toLocaleDateString(
                                'default',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  timeZone: 'UTC'
                                }
                              )}
                            </Text>
                          </Flex>
                          <Button
                            data-testid="delete-creative-btn"
                            className="delete-btn"
                            emphasis="low"
                            onClick={removeFile}
                            lss={{
                              borderRadius: 'rounded',
                              display: 'none',
                              border: 'none'
                            }}
                          >
                            <Icon
                              size={20}
                              as={MdDeleteOutline}
                              lss={{ color: 'danger500' }}
                            />
                          </Button>
                        </Flex>
                      </Flex>
                    ) : (
                      uploadFileContent
                    )}
                  </>
                )}
              </Flex>

              <Flex
                lss={{
                  justifyContent: 'end',
                  gap: '8px'
                }}
              >
                <Button
                  data-testid="upload-creative-close"
                  type="button"
                  onClick={() => {
                    removeFile();
                    handleToggle();
                  }}
                  emphasis="medium"
                >
                  {t('campaignManager.actions.close')}
                </Button>
                <Button
                  data-testid="upload-creative-submit"
                  type="submit"
                  disabled={isSaveDisabled}
                >
                  {t('campaignManager.actions.save')}
                </Button>
              </Flex>
            </Form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
